import { Accordion, Heading, Table } from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage } from '@zg-rentals/ts-satellite-client-react-query';
import type {
  SatelliteWebLandlordGetLatestForContactsPageConversationDto,
  SatelliteWebLandlordGetLatestForContactsPageResponseDto,
  SatelliteWebLandlordGetLatestRenterProfileDto,
} from '@zg-rentals/ts-satellite-client';
import type { OnContactsPageConversationNoteUpdate } from '../../utils/contactsPageConversationsQueryParams';
import { useAuth } from '../../AuthProvider';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ModalType } from '../../types/ModalType';
import useModal from '../../hooks/useModal';
import { useContactsPageConversationsQueryParams } from '../../hooks/useContactsPageConversationsQueryParams';
import { MultiFamilyLeadsPageContext } from '../../contexts/MultiFamilyLeadsContextProvider';
import NotesModal from '../Modals/NotesModal';
import RenterProfileModal from '../Modals/RenterProfileModal';
import SendApplicationConfirmationModal from '../Modals/SendApplicationConfirmationModal';
import SendMessageModal from '../Modals/SendMessageModal';
import TableContentWrapper from './components/TableContentWrapper';
import TableFooter, { DEFAULT_ROWS_PER_PAGE } from './components/TableFooter';
import { Skeleton } from './Skeleton';
import {
  ACCORDION_CLICK_TO_OPEN_EVENT_TYPE_ID,
  emitAccordionClickToOpenEvent,
} from '../../constants/clickstreamEvents/multiFamilyLeadsPage';
import TableContent from './components/TableContent';

/** Backend rejects start values above this (e.g. "invalid start value=110"). Cap requests and treat as last page. */
const MAX_PAGINATION_START = 100;

export const TABLE_COLUMNS = [
  'Name',
  'Contact info',
  'Move-in date',
  'Renter profile',
  'Latest contact',
  'Notes',
  'Prospect stage',
  'Action',
] as const;

type OpenModalArgs = {
  conversationId: string;
  listingAlias: string;
  modalType: ModalType;
  // Optional conversation data for modals
  conversation?: SatelliteWebLandlordGetLatestForContactsPageConversationDto;
  renterName?: string;
  renterProfile?: SatelliteWebLandlordGetLatestRenterProfileDto;
};

export type OpenModal = ({
  conversationId,
  listingAlias,
  modalType,
  conversation,
  renterName,
  renterProfile,
}: OpenModalArgs) => void;

export default function LeadsTable() {
  const { listingAlias, clientTimeZone, daysBack, startDate } = useContext(MultiFamilyLeadsPageContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();
  const { isEnterpriseCustomer } = user || {};
  const queryClient = useQueryClient();

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentConversationId, setCurrentConversationId] = useState('');
  const [currentListingAlias, setCurrentListingAlias] = useState(listingAlias || '');
  const [refetchCount, setRefetchCount] = useState(0);
  const [currentConversation, setCurrentConversation] =
    useState<SatelliteWebLandlordGetLatestForContactsPageConversationDto>();
  const [currentRenterName, setCurrentRenterName] = useState<string>();
  const [currentRenterProfile, setCurrentRenterProfile] = useState<SatelliteWebLandlordGetLatestRenterProfileDto>();

  const currentStart = (currentPageNumber - 1) * currentRowsPerPage;
  const cappedStart = Math.min(currentStart, MAX_PAGINATION_START);
  const contactsPageQueryParams = useContactsPageConversationsQueryParams(currentRowsPerPage, cappedStart);

  const onContactsPageConversationNoteUpdate = useCallback<OnContactsPageConversationNoteUpdate>(
    (conversationId, noteUpdate) => {
      if (!contactsPageQueryParams) return;
      const queryKey =
        useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage.getKey(
          contactsPageQueryParams,
        );
      queryClient.setQueryData<SatelliteWebLandlordGetLatestForContactsPageResponseDto>(queryKey, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          conversations: oldData.conversations?.map((conv) =>
            conv.conversationId === conversationId
              ? {
                  ...conv,
                  note: {
                    ...conv.note,
                    ...noteUpdate,
                  },
                }
              : conv,
          ),
        };
      });
    },
    [contactsPageQueryParams, queryClient],
  );

  const {
    data: conversationsData,
    isLoading,
    error,
    refetch: refetchConversations,
  } = useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage(
    // contactsPageQueryParams is always defined when LeadsTable is rendered (contacts page has MultiFamilyLeadsPageContext)
    contactsPageQueryParams!,
    {
      // TODO RRMP-5713: Update staleTime to staleTime: 5 * 60 * 1000 (5 minutes) once LeadSummaryCounts is using react-query so that they match
      staleTime: Infinity, // Keep in sync with LeadSummaryCounts that are only fetched once
      retry: 2,
      meta: {
        context: {
          api: 'getLatestConversationsForContactsPage',
          details: 'LeadsTable - Error fetching conversations',
        },
      },
    },
  );

  const conversations = conversationsData?.conversations || [];
  const hasNextPage = conversationsData?.hasNextPage ?? false;

  const hasError = !!error || (!conversationsData && !isLoading);
  const tableRef = useRef<HTMLDivElement>(null);
  const conversationsToDisplay = conversations;
  const skeletonRows = Array(DEFAULT_ROWS_PER_PAGE).fill('');
  const hasNoLeads = !error && !isLoading && conversationsToDisplay.length === 0;
  const isAtPaginationCap = currentStart >= MAX_PAGINATION_START && !hasError;
  const isLastPage = !hasNextPage || currentStart >= MAX_PAGINATION_START;

  useEffect(() => {
    // Reset pagination state when filters change
    setCurrentPageNumber(1);
    setCurrentRowsPerPage(DEFAULT_ROWS_PER_PAGE);
  }, [listingAlias, daysBack, startDate]);

  const handleNextPageClick = () => {
    tableRef.current?.scrollIntoView();
    setCurrentPageNumber((prev) => prev + 1);
  };

  const handlePreviousPageClick = () => {
    tableRef.current?.scrollIntoView();
    setCurrentPageNumber((prev) => Math.max(1, prev - 1));
  };

  const handleRowsPerPageClick = (selectedRowsPerPage: number) => {
    tableRef.current?.scrollIntoView();
    setCurrentPageNumber(1);
    setCurrentRowsPerPage(selectedRowsPerPage);
  };

  const openModal: OpenModal = ({
    conversationId,
    listingAlias,
    modalType,
    conversation,
    renterName,
    renterProfile,
  }) => {
    setCurrentConversationId(conversationId);
    setCurrentListingAlias(listingAlias);
    setActiveModal(modalType);
    if (conversation) {
      setCurrentConversation(conversation);
    }
    if (renterName !== undefined) {
      setCurrentRenterName(renterName);
    }
    if (renterProfile) {
      setCurrentRenterProfile(renterProfile);
    }
  };

  const handleAccordionChange = (value: string | Array<string>) => {
    const isAccordionOpen = !!value.length || value === 'leads-table';

    if (isAccordionOpen) {
      emitAccordionClickToOpenEvent(user);

      // Refetch 3 times max if accordion is open AND there are no conversations, or if there is an error.
      if (refetchCount < 3 && (!conversationsToDisplay.length || error)) {
        setRefetchCount(refetchCount + 1);
        return refetchConversations();
      }
    }
  };

  return (
    <>
      {activeModal === ModalType.RenterProfileModal && (
        <RenterProfileModal renterName={currentRenterName} renterProfile={currentRenterProfile} />
      )}

      {activeModal === ModalType.NotesModal && (
        <NotesModal
          clientTimeZone={clientTimeZone}
          conversationId={currentConversationId}
          listingAlias={currentListingAlias}
          onContactsPageConversationNoteUpdate={onContactsPageConversationNoteUpdate}
          shouldRefetchStatusLabels={false}
        />
      )}

      {activeModal === ModalType.SendApplicationConfirmationModal && (
        <SendApplicationConfirmationModal
          conversationId={currentConversationId}
          clientTimeZone={clientTimeZone}
          leadDisplayName={currentRenterName || '-'}
          listingAlias={currentListingAlias}
        />
      )}

      {activeModal === ModalType.SendMessageModal && (
        <SendMessageModal
          conversationId={currentConversationId}
          listingAlias={currentListingAlias}
          conversation={currentConversation}
          currentRowsPerPage={currentRowsPerPage}
          currentStart={cappedStart}
        />
      )}

      <Accordion.Root
        data-eventid={ACCORDION_CLICK_TO_OPEN_EVENT_TYPE_ID}
        onExpandedChange={handleAccordionChange}
        css={{ marginTop: 'loose', marginBottom: 'looser' }}
      >
        <Accordion.Item value="leads-table">
          <Accordion.Header>
            <Heading level={2} textStyle="heading-xs">
              Recent conversations
            </Heading>
          </Accordion.Header>

          <Accordion.Panel ref={tableRef}>
            <TableContentWrapper
              hasError={hasError}
              hasNoLeads={hasNoLeads}
              isAtPaginationCap={isAtPaginationCap}
              isEnterpriseCustomer={Boolean(isEnterpriseCustomer)}
            >
              <Table.Header>
                <Table.Row>
                  {TABLE_COLUMNS.map((columnName) => (
                    <Table.HeaderCell
                      key={columnName}
                      data-isSticky={columnName === 'Name'}
                      css={{
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                        '&[data-isSticky="true"]': {
                          position: 'sticky',
                          left: 0,
                        },
                      }}
                    >
                      {columnName}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>

              {isLoading ? (
                <Table.Body>
                  {skeletonRows.map((_, index) => (
                    <Skeleton key={index} />
                  ))}
                </Table.Body>
              ) : (
                <TableContent conversations={conversationsToDisplay} openModal={openModal} />
              )}
            </TableContentWrapper>

            <TableFooter
              currentPageNumber={currentPageNumber}
              currentRowsPerPage={currentRowsPerPage}
              handleNextPageClick={handleNextPageClick}
              handlePreviousPageClick={handlePreviousPageClick}
              handleRowsPerPageClick={handleRowsPerPageClick}
              hasError={hasError}
              isLastPage={isLastPage}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion.Root>
    </>
  );
}
